import type { SavedViewConfig } from "@/components/shared/UniversalCRMTable/types";
import { collectFieldsForSavedView } from "@/features/tasks/config/collect-fields-for-saved-view";
import { TASK_FIELDS, type TaskFieldId } from "./task-field-ids";

/**
 * Predefined saved views for the Tasks table.
 *
 * `visibleColumns` is derived from each field's `savedViewMembership`
 * declaration via `collectFieldsForSavedView` — so adding or removing a
 * column from a view only requires touching the relevant field file.
 *
 * Typed against `TaskFieldId` so the helper output is compile-time validated
 * against the entity's actual field map.
 */
const visibleFor = (viewId: string): TaskFieldId[] =>
  collectFieldsForSavedView<TaskFieldId>(viewId, TASK_FIELDS);

export const taskSavedViews: SavedViewConfig<TaskFieldId>[] = [
  {
    id: "all-tasks",
    name: "All Tasks",
    filters: {},
    sort: { field: "created_at", direction: "desc" },
    visibleColumns: visibleFor("all-tasks"),
  },
  {
    id: "my-tasks",
    name: "My Tasks",
    filters: { assigned_users: "me" },
    sort: { field: "due_date", direction: "asc" },
    visibleColumns: visibleFor("my-tasks"),
  },
  {
    id: "high-priority",
    name: "High Priority",
    filters: { priority: "high" },
    sort: { field: "due_date", direction: "asc" },
    visibleColumns: visibleFor("high-priority"),
  },
  {
    id: "overdue-tasks",
    name: "Overdue Tasks",
    filters: { due_date: "overdue" },
    sort: { field: "due_date", direction: "asc" },
    visibleColumns: visibleFor("overdue-tasks"),
  },
  {
    id: "pending-tasks",
    name: "All Pending",
    filters: { status: "pending" },
    sort: { field: "due_date", direction: "asc" },
    visibleColumns: visibleFor("pending-tasks"),
  },
  {
    id: "in-progress-tasks",
    name: "All In Progress",
    filters: { status: "in_progress" },
    sort: { field: "due_date", direction: "asc" },
    visibleColumns: visibleFor("in-progress-tasks"),
  },
  {
    id: "completed-tasks",
    name: "All Completed",
    filters: { status: "completed" },
    sort: { field: "due_date", direction: "desc" },
    visibleColumns: visibleFor("completed-tasks"),
  },
  {
    id: "cancelled-tasks",
    name: "All Cancelled",
    filters: { status: "cancelled" },
    sort: { field: "created_at", direction: "desc" },
    visibleColumns: visibleFor("cancelled-tasks"),
  },
];
